import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Zap, CheckCircle2, TrendingUp } from "lucide-react";
import { scrollToId, startCheckout } from "@/lib/api";

const HERO_IMG = "/assets/foto-libera-hero.jpg";

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const buy = async () => {
    setLoading(true);
    try {
      await startCheckout();
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 pb-20 sm:pt-40 lg:pb-28">
      <div className="pointer-events-none absolute -top-40 right-[-15%] h-[36rem] w-[36rem] rounded-full bg-rose/30 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[28rem] w-[28rem] rounded-full bg-sage/15 blur-3xl" />

      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.15fr_1fr]">
        <motion.div style={{ opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-terracotta/25 bg-terracotta/5 px-4 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-terracotta" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">
              Metodo pratico · Percorso di 7 giorni
            </span>
          </motion.div>

          <h1 className="font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>La tua <em className="italic text-terracotta">prima ora</em></MaskedLine>
            <MaskedLine delay={0.4}>di libertà inizia</MaskedLine>
            <MaskedLine delay={0.55}>quando smetti di fare</MaskedLine>
            <MaskedLine delay={0.7}><span className="italic">tutto da sola.</span></MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-taupe sm:text-lg"
          >
            Costruisci un'attività solida, senza diventare prigioniera del tuo lavoro.
            Il percorso pratico di <strong className="font-semibold text-ink">7 giorni</strong> per imparare a
            delegare, organizzare la tua azienda e recuperare tempo per te e per chi ami.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={buy}
              disabled={loading}
              data-testid="hero-buy-button"
              className="group rounded-full bg-terracotta px-8 py-4 text-base font-semibold text-linen shadow-xl shadow-terracotta/30 transition-all hover:-translate-y-0.5 hover:bg-terracotta-dark hover:shadow-2xl hover:shadow-terracotta/40 disabled:opacity-60"
            >
              {loading ? "Ti porto al pagamento…" : "Voglio la mia prima ora di libertà · €29"}
            </button>
            <button
              onClick={() => scrollToId("quiz")}
              data-testid="hero-quiz-button"
              className="group flex items-center gap-2 rounded-full border border-ink/15 px-6 py-4 text-sm font-semibold text-ink transition-colors hover:border-terracotta hover:text-terracotta"
            >
              Fai il quiz di 2 minuti
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-stone2"
          >
            <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-terracotta" /> Accesso immediato</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-terracotta" /> 100% pratico, zero teoria</span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div className="overflow-hidden rounded-t-[12rem] rounded-b-[2rem] border border-ink/10 shadow-2xl shadow-espresso/20">
              <img
                src={HERO_IMG}
                alt="Hellen Gebremeskel, fondatrice del metodo Prima Ora di Libertà"
                className="aspect-[4/5] w-full object-cover"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="absolute -bottom-8 -left-4 rounded-2xl border border-ink/5 bg-white/90 p-5 shadow-xl backdrop-blur-md sm:-left-10"
            >
              <p className="flex items-center gap-2 font-serif text-2xl text-terracotta">
                <TrendingUp className="h-5 w-5" /> Level up
              </p>
              <p className="mt-1 text-xs font-medium uppercase tracking-widest text-taupe">da tutto-da-sola a libera</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
